import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Button,
	TextField,
	Typography,
	Switch,
	FormControlLabel,
	Backdrop,
	CircularProgress,
	Autocomplete,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config.json";
import { useUser } from "../contexts/UserContext";

const CustomerDetails = () => {
	const navigate = useNavigate();
	const { userRole } = useUser();
	const location = useLocation();
	const [customerId, setCustomerId] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [successMessage, setSuccessMessage] = useState("");
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phone: "",
		sourceList: [],
		targetList: [],
		totalTransactionsPurchased: "",
		autoRenew: false,
		renewThreshold: "",
	});
	const [sourceOptions, setSourceOptions] = useState([]);
	const [targetOptions, setTargetOptions] = useState([]);

	const nameRef = useRef(null);
	const phoneRef = useRef(null);

	useEffect(() => {
		if (userRole !== "admin") {
			handleLogout();
		}
	}, [userRole, navigate]);

	const handleLogout = () => {
		localStorage.removeItem("authToken");
		localStorage.removeItem("userData");
		navigate("/login");
	};

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const customerId = queryParams.get("customerId");

		if (customerId) {
			setCustomerId(customerId);
			setIsEditMode(true);
			fetchCustomerDetails(customerId);
		} else {
			setIsEditMode(false);
		}
	}, [location.search]);

	useEffect(() => {
		if (isEditMode) {
			phoneRef.current.focus();
		} else {
			nameRef.current.focus();
		}
	}, [isEditMode]);

	const fetchCustomerDetails = async (customerId) => {
		setLoading(true);
		setError(null);

		try {
			const response = await axios.get(
				`${config.baseURL}middlewareCustomers/${customerId}`,
				{
					headers: {
						...config.headers,
					},
				}
			);

			const customerData = response.data[0];
			console.log("Fetched customer data:", customerData);

			// Deduplicate source and target lists
			const dedupedSourceList = Array.isArray(customerData.Source_List)
				? customerData.Source_List.filter(
						(item, idx, arr) =>
							arr.findIndex((i) => i.Source_Name === item.Source_Name) === idx
				  )
				: [];
			const dedupedTargetList = Array.isArray(customerData.Target_List)
				? customerData.Target_List.filter(
						(item, idx, arr) =>
							arr.findIndex((i) => i.Target_Name === item.Target_Name) === idx
				  )
				: [];

			setFormData({
				name: customerData.Name || "",
				email: customerData.Email || "",
				phone: customerData.Phone || "",
				sourceList: dedupedSourceList,
				targetList: dedupedTargetList,
				totalTransactionsPurchased:
					customerData.Total_Transactions_Purchased || "",
				autoRenew: customerData.Auto_Renew || false,
				renewThreshold: customerData.Renew_Threshold || "",
			});
		} catch (error) {
			console.error("Error fetching customer details:", error);
			setError("Failed to load customer details. Please try again later.");
		} finally {
			setLoading(false);
		}
	};

	const fetchSourceOptions = async () => {
		try {
			const response = await axios.get(`${config.baseURL}middlewareSources`, {
				headers: {
					...config.headers,
				},
			});

			const formattedSourceOptions = response.data
				.map((option) => ({
					Source_Name: option.name,
				}))
				.sort((a, b) => a.Source_Name.localeCompare(b.Source_Name));

			setSourceOptions(formattedSourceOptions);
		} catch (error) {
			console.error("Error fetching source options:", error);
		}
	};

	const fetchTargetOptions = async () => {
		try {
			const response = await axios.get(`${config.baseURL}middlewareTargets`, {
				headers: {
					...config.headers,
				},
			});

			const formattedTargetOptions = response.data
				.map((option) => ({
					Target_Name: option.name,
				}))
				.sort((a, b) => a.Target_Name.localeCompare(b.Target_Name));

			setTargetOptions(formattedTargetOptions);
		} catch (error) {
			console.error("Error fetching target options:", error);
		}
	};

	useEffect(() => {
		fetchSourceOptions();
		fetchTargetOptions();
	}, []);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleToggleChange = (e) => {
		const { name, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: checked,
			renewThreshold:
				name === "autoRenew" && !checked ? "" : prev.renewThreshold,
		}));
	};

	const handleSourceChange = (event, value) => {
		// Remove duplicates by Source_Name
		const deduped = value.filter(
			(item, idx, arr) =>
				arr.findIndex((i) => i.Source_Name === item.Source_Name) === idx
		);
		setFormData((prev) => ({ ...prev, sourceList: deduped }));
	};

	const handleTargetChange = (event, value) => {
		// Remove duplicates by Target_Name
		const deduped = value.filter(
			(item, idx, arr) =>
				arr.findIndex((i) => i.Target_Name === item.Target_Name) === idx
		);
		setFormData((prev) => ({ ...prev, targetList: deduped }));
	};

	const validateEmail = (email) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(String(email).toLowerCase());
	};

	const validateFormData = () => {
		if (!formData.name) {
			setError("Name is mandatory");
			return false;
		}

		if (!validateEmail(formData.email)) {
			setError("Invalid email format");
			return false;
		}

		if (formData.sourceList.length === 0) {
			setError("Source List is mandatory");
			return false;
		}

		if (formData.targetList.length === 0) {
			setError("Target List is mandatory");
			return false;
		}

		return true;
	};

	const handleCreateCustomer = async () => {
		if (!validateFormData()) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const customerRequestBody = {
				Name: formData.name,
				Email: formData.email,
				Phone: formData.phone,
				Source_List: formData.sourceList,
				Target_List: formData.targetList,
				Total_Transactions_Purchased: parseInt(
					formData.totalTransactionsPurchased,
					10
				),
				Auto_Renew: formData.autoRenew,
				Renew_Threshold: parseInt(formData.renewThreshold, 10),
			};

			const customerResponse = await axios.post(
				`${config.baseURL}middlewareCustomers`,
				customerRequestBody,
				{
					headers: {
						...config.headers,
					},
				}
			);

			const createdCustomer = customerResponse.data;
			console.log("Created customer response:", createdCustomer);

			// const customerId = Array.isArray(createdCustomer.data)
			// 	? createdCustomer.data[0]?.id
			// 	: createdCustomer.data?.id;
			const customerId = createdCustomer.upsertedId;
			console.log("Created customer id:", customerId);

			const passwordHash = Math.floor(
				100000 + Math.random() * 900000
			).toString();

			const userRequestBody = {
				first_name: formData.name,
				last_name: "",
				email: formData.email,
				password_hash: passwordHash,
				role: "user",
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
				customer_id: customerId,
			};

			console.log("Creating user with request body:", userRequestBody);

			await axios.post(`${config.baseURL}middlewareUsers`, userRequestBody, {
				headers: {
					...config.headers,
				},
			});

			setSuccessMessage("Customer created successfully!");
			setTimeout(() => {
				setSuccessMessage("");
				navigate("/customer-management");
			}, 300);
		} catch (error) {
			console.error("Error during submission:", error);
			let errorMessage = "An unexpected error occurred";

			if (axios.isAxiosError(error)) {
				console.error("API Error Response:", error.response?.data);
				errorMessage = error.response?.data?.message || error.message;
			}

			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleEditCustomer = async () => {
		if (!validateFormData()) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const customerRequestBody = {
				Name: formData.name,
				Email: formData.email,
				Phone: formData.phone,
				Source_List: formData.sourceList,
				Target_List: formData.targetList,
				Total_Transactions_Purchased: parseInt(
					formData.totalTransactionsPurchased,
					10
				),
				Auto_Renew: formData.autoRenew,
				Renew_Threshold: parseInt(formData.renewThreshold, 10),
			};

			await axios.patch(
				`${config.baseURL}middlewareCustomers/${customerId}`,
				customerRequestBody,
				{
					headers: {
						...config.headers,
					},
				}
			);

			setSuccessMessage("Customer updated successfully!");
			setTimeout(() => {
				setSuccessMessage("");
				navigate("/customer-management");
			}, 300);
		} catch (error) {
			console.error("Error during update:", error);
			let errorMessage = "An unexpected error occurred";

			if (axios.isAxiosError(error)) {
				console.error("API Error Response:", error.response?.data);
				errorMessage = error.response?.data?.message || error.message;
			}

			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const renderFormFields = () => {
		return (
			<>
				<TextField
					label="Name"
					variant="outlined"
					fullWidth
					margin="normal"
					name="name"
					value={formData.name}
					onChange={handleInputChange}
					required
					disabled={isEditMode}
					inputRef={nameRef}
				/>
				<TextField
					label="Email"
					variant="outlined"
					fullWidth
					margin="normal"
					name="email"
					value={formData.email}
					onChange={handleInputChange}
					error={formData.email && !validateEmail(formData.email)}
					helperText={
						formData.email && !validateEmail(formData.email)
							? "Invalid email format"
							: ""
					}
					required
					disabled={isEditMode}
				/>
				<TextField
					label="Phone"
					variant="outlined"
					fullWidth
					margin="normal"
					name="phone"
					value={formData.phone}
					onChange={handleInputChange}
					inputRef={phoneRef}
				/>
				<Autocomplete
					multiple
					options={sourceOptions}
					getOptionLabel={(option) => option.Source_Name}
					value={formData.sourceList}
					onChange={handleSourceChange}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							label="Source List"
							margin="normal"
							required
						/>
					)}
				/>
				<Autocomplete
					multiple
					options={targetOptions}
					getOptionLabel={(option) => option.Target_Name}
					value={formData.targetList}
					onChange={handleTargetChange}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							label="Target List"
							margin="normal"
							required
						/>
					)}
				/>
				<TextField
					label="Total Transactions Purchased"
					variant="outlined"
					fullWidth
					margin="normal"
					name="totalTransactionsPurchased"
					value={formData.totalTransactionsPurchased}
					onChange={handleInputChange}
					type="number"
				/>
				<FormControlLabel
					control={
						<Switch
							checked={formData.autoRenew}
							onChange={handleToggleChange}
							name="autoRenew"
							color="primary"
						/>
					}
					label="Auto Renew"
				/>
				<TextField
					label="Renew Threshold"
					variant="outlined"
					fullWidth
					margin="normal"
					name="renewThreshold"
					value={formData.renewThreshold}
					onChange={handleInputChange}
					type="number"
					disabled={!formData.autoRenew}
				/>
			</>
		);
	};

	const isFormValid = () => {
		return (
			formData.name &&
			validateEmail(formData.email) &&
			formData.sourceList.length > 0 &&
			formData.targetList.length > 0
		);
	};

	const handleBackButtonClick = () => {
		navigate("/customer-management");
	};

	return (
		<Box sx={{ width: "80%", margin: "20px auto", padding: "20px" }}>
			<Box sx={{ mt: 2 }}>{renderFormFields()}</Box>
			<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
				<Button
					variant="outlined"
					color="secondary"
					onClick={handleBackButtonClick}
				>
					Back
				</Button>
				<Button
					variant="contained"
					sx={{ backgroundColor: "#FF7600", color: "#2c2d2d" }}
					onClick={isEditMode ? handleEditCustomer : handleCreateCustomer}
					disabled={!isFormValid()}
				>
					<strong>{isEditMode ? "Update Customer" : "Create Customer"}</strong>
				</Button>
			</Box>
			{successMessage && (
				<Typography
					sx={{
						color: "green",
						textAlign: "center",
						mt: 2,
					}}
				>
					{successMessage}
				</Typography>
			)}
			{error && (
				<Typography
					sx={{
						color: "red",
						textAlign: "center",
						mt: 2,
					}}
				>
					{error}
				</Typography>
			)}
			<Backdrop
				sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
		</Box>
	);
};

export default CustomerDetails;
